/**
 * The reminder engine.
 *
 * This is the differentiator described in `01-PROJECT-BRIEF.md`, and this file is the whole of
 * its decision-making: given the portfolio as it stands today, which reminders are due to go
 * out, to whom, on what channel, and under what dedupe key.
 *
 * It is pure. It reads no database and sends nothing. The caller loads the candidate rows,
 * calls `planReminders`, and enqueues what comes back — which means the engine can be tested
 * exhaustively against fabricated portfolios, and a manager can be shown exactly what *would*
 * be sent before anything is.
 */

import { addDays, diffDays, type PlainDate } from "./dates";
import { balanceOf, isSettled, type InvoiceStatus } from "./billing";
import type { TemplateChannel } from "./templates";
import type { Minor } from "./money";

export type ReminderTrigger =
  | "rent_due"
  | "rent_overdue"
  | "lease_expiring"
  | "lease_expired"
  | "deposit_refund_due"
  | "maintenance_sla";

export interface TriggerInfo {
  value: ReminderTrigger;
  label: string;
  description: string;
  /** Whether the offset counts before (`-`) or after (`+`) the anchor date. */
  direction: "before" | "after";
  /** Reminders about staff work go to staff, not to the tenant. */
  audience: "tenant" | "staff";
}

const TRIGGER_INFO = new Map<ReminderTrigger, TriggerInfo>();

export function triggerInfo(trigger: ReminderTrigger): TriggerInfo {
  const info = TRIGGER_INFO.get(trigger);
  if (info) return info;
  return { value: trigger, label: trigger, description: "", direction: "after", audience: "tenant" };
}

export const REMINDER_TRIGGERS: TriggerInfo[] = [
  {
    value: "rent_due",
    label: "Rent due",
    description: "Before an invoice falls due.",
    direction: "before",
    audience: "tenant",
  },
  {
    value: "rent_overdue",
    label: "Rent overdue",
    description: "After an invoice has passed its due date and is still unpaid.",
    direction: "after",
    audience: "tenant",
  },
  {
    value: "lease_expiring",
    label: "Tenancy expiring",
    description: "Before a tenancy ends, so the renewal is agreed in time.",
    direction: "before",
    audience: "tenant",
  },
  {
    value: "lease_expired",
    label: "Tenancy expired",
    description: "After a tenancy has ended with no renewal recorded.",
    direction: "after",
    audience: "tenant",
  },
  {
    value: "deposit_refund_due",
    label: "Deposit refund due",
    description: "Before a caution deposit must be returned after a move-out.",
    direction: "before",
    audience: "staff",
  },
  {
    value: "maintenance_sla",
    label: "Maintenance overdue",
    description: "When a maintenance request passes its response target.",
    direction: "after",
    audience: "staff",
  },
];

for (const info of REMINDER_TRIGGERS) TRIGGER_INFO.set(info.value, info);

export interface ReminderRule {
  id: string;
  trigger: ReminderTrigger;
  /** Days before or after the anchor, as a positive number. Direction comes from the trigger. */
  offsetDays: number;
  channel: TemplateChannel;
  templateId: string | null;
  enabled: boolean;
}

/**
 * What a new agency starts with. Chosen so that the very first automatic run does something
 * visibly useful, and so that nobody has to design a chase sequence from a blank page.
 */
export const DEFAULT_REMINDER_RULES: Array<Omit<ReminderRule, "id" | "templateId">> = [
  { trigger: "rent_due", offsetDays: 30, channel: "email", enabled: true },
  { trigger: "rent_due", offsetDays: 14, channel: "email", enabled: true },
  { trigger: "rent_due", offsetDays: 7, channel: "email", enabled: true },
  { trigger: "rent_due", offsetDays: 1, channel: "email", enabled: true },
  { trigger: "rent_overdue", offsetDays: 1, channel: "email", enabled: true },
  { trigger: "rent_overdue", offsetDays: 7, channel: "email", enabled: true },
  { trigger: "rent_overdue", offsetDays: 14, channel: "email", enabled: true },
  { trigger: "rent_overdue", offsetDays: 30, channel: "email", enabled: true },
  { trigger: "lease_expiring", offsetDays: 90, channel: "email", enabled: true },
  { trigger: "lease_expiring", offsetDays: 60, channel: "email", enabled: true },
  { trigger: "lease_expiring", offsetDays: 30, channel: "email", enabled: true },
  { trigger: "lease_expiring", offsetDays: 14, channel: "email", enabled: true },
  { trigger: "lease_expired", offsetDays: 1, channel: "email", enabled: true },
  { trigger: "lease_expired", offsetDays: 7, channel: "email", enabled: true },
];

/* ---------------------------------------------------------------- candidates */

export interface InvoiceCandidate {
  kind: "invoice";
  invoiceId: string;
  leaseId: string | null;
  partyId: string;
  dueDate: PlainDate;
  total: Minor;
  paid: Minor;
  status: InvoiceStatus;
}

export interface LeaseCandidate {
  kind: "lease";
  leaseId: string;
  partyId: string;
  endDate: PlainDate | null;
  status: string;
}

export type ReminderCandidate = InvoiceCandidate | LeaseCandidate;

export interface PlannedReminder {
  ruleId: string;
  trigger: ReminderTrigger;
  channel: TemplateChannel;
  templateId: string | null;
  partyId: string;
  invoiceId: string | null;
  leaseId: string | null;
  /** The date the reminder is *about* — due date or expiry date. */
  anchorDate: PlainDate;
  offsetDays: number;
  /**
   * The idempotency key. One row per (rule, subject, anchor) ever leaves the system, so a
   * cron that runs twice, or a job that retries after a crash, cannot double-message a tenant.
   */
  dedupeKey: string;
}

export interface PlanOptions {
  asOf: PlainDate;
  rules: ReminderRule[];
  candidates: ReminderCandidate[];
  /** Dedupe keys already sent. Anything matching is dropped. */
  alreadySent?: Iterable<string>;
  /** Invoice grace days, so a rule does not fire during the grace window. */
  graceDays?: number;
  /**
   * Whether at most one message per tenant per trigger may be planned in a run. On by default.
   *
   * The cap exists for one reason: four identical emails in the same morning is how a reminder
   * system trains people to ignore it (ADR-0014). It is the right answer while every message is
   * about one invoice — and the wrong one when the caller is going to fold the whole run into a
   * single message per person anyway.
   *
   * With the cap on, a client four arrears behind advances **one** invoice down the ladder per
   * run, and the other three are chased on the following mornings instead — six separate mornings
   * rather than six separate emails, which is not much of an improvement. So `per_person` turns
   * the cap off, plans every arrear at its own correct step, and posts them in one envelope.
   * Nothing is sent that the engine would not have sent; it is sent once instead of six times.
   */
  capPerPerson?: boolean;
}

export function planReminders(opts: PlanOptions): PlannedReminder[] {
  const { asOf, rules, candidates, graceDays = 0, capPerPerson = true } = opts;
  const sent = new Set(opts.alreadySent ?? []);
  const planned: PlannedReminder[] = [];
  const seen = new Set<string>();

  // The furthest-along chase already sent for each (trigger, subject). This is what makes the
  // "after" triggers catch up correctly on an imported portfolio — see `matches` below.
  const reached = highestOffsetSent([...sent, ...seen]);

  // Largest offset first, so a debt that is already months old gets the *last* message in the
  // sequence rather than the first one.
  const ordered = [...rules].sort((a, b) => b.offsetDays - a.offsetDays);

  // At most one message per tenant per trigger per run. A tenant four monthly invoices behind
  // needs one chase, not four identical emails in the same minute — and receiving four is how a
  // reminder system trains people to ignore it.
  const messagedThisRun = new Set<string>();

  for (const rule of ordered) {
    if (!rule.enabled) continue;

    for (const candidate of candidates) {
      const hit = matches(rule, candidate, asOf, graceDays);
      if (!hit) continue;

      // For an "after" trigger, skip anything softer than a chase already sent. Without this an
      // invoice imported 200 days overdue would fire the 1-, 7-, 14- and 30-day messages all at
      // once, and a tenant would receive four escalating letters in one morning.
      const direction = triggerInfo(rule.trigger).direction;
      if (direction === "after") {
        const already = reached.get(`${rule.trigger}:${hit.subjectId}`) ?? -1;
        if (already >= rule.offsetDays) continue;
        reached.set(`${rule.trigger}:${hit.subjectId}`, rule.offsetDays);
      }

      const perPersonKey = `${rule.trigger}:${candidate.partyId}`;
      if (capPerPerson && triggerInfo(rule.trigger).audience === "tenant" && messagedThisRun.has(perPersonKey)) continue;

      const dedupeKey = keyFor(rule, hit.subjectId, hit.anchorDate);
      if (sent.has(dedupeKey) || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      messagedThisRun.add(perPersonKey);

      planned.push({
        ruleId: rule.id,
        trigger: rule.trigger,
        channel: rule.channel,
        templateId: rule.templateId,
        partyId: candidate.partyId,
        invoiceId: candidate.kind === "invoice" ? candidate.invoiceId : null,
        leaseId: candidate.kind === "invoice" ? candidate.leaseId : candidate.leaseId,
        anchorDate: hit.anchorDate,
        offsetDays: rule.offsetDays,
        dedupeKey,
      });
    }
  }

  return planned;
}

function matches(
  rule: ReminderRule,
  candidate: ReminderCandidate,
  asOf: PlainDate,
  graceDays: number,
): { subjectId: string; anchorDate: PlainDate } | null {
  if (rule.trigger === "rent_due" || rule.trigger === "rent_overdue") {
    if (candidate.kind !== "invoice") return null;
    if (isSettled(candidate.status) || balanceOf(candidate) <= 0) return null;
    if (candidate.status === "draft") return null;

    if (rule.trigger === "rent_due") {
      // Fires exactly `offsetDays` before the due date — not "within", or every run would fire.
      if (diffDays(asOf, candidate.dueDate) !== rule.offsetDays) return null;
    } else {
      // Overdue fires at *or after* the offset, so an arrear that was already months old when
      // the portfolio was imported still gets chased. The caller's `reached` map stops the whole
      // ladder going out at once.
      const lateFrom = addDays(candidate.dueDate, graceDays);
      if (diffDays(lateFrom, asOf) < rule.offsetDays) return null;
    }
    return { subjectId: candidate.invoiceId, anchorDate: candidate.dueDate };
  }

  if (rule.trigger === "lease_expiring" || rule.trigger === "lease_expired") {
    if (candidate.kind !== "lease" || !candidate.endDate) return null;
    if (candidate.status !== "active" && candidate.status !== "expiring" && candidate.status !== "expired") return null;

    const delta = diffDays(asOf, candidate.endDate);
    if (rule.trigger === "lease_expiring" && delta !== rule.offsetDays) return null;
    // Same catch-up rule as overdue rent: a tenancy that expired long ago must still be chased.
    if (rule.trigger === "lease_expired" && -delta < rule.offsetDays) return null;
    return { subjectId: candidate.leaseId, anchorDate: candidate.endDate };
  }

  return null;
}

/**
 * The furthest chase already sent per (trigger, subject), read back out of the dedupe keys.
 *
 * The key format is `trigger:offset:ruleId:subject:anchor`, so no extra storage is needed to
 * answer "how far down the ladder have we already gone with this tenant?".
 */
function highestOffsetSent(keys: string[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const key of keys) {
    const parts = key.split(":");
    if (parts.length < 5) continue; // manual sends and anything unrecognised
    const [trigger, offsetRaw, , subjectId] = parts;
    const offset = Number(offsetRaw);
    if (!Number.isFinite(offset)) continue;
    const mapKey = `${trigger}:${subjectId}`;
    out.set(mapKey, Math.max(out.get(mapKey) ?? -1, offset));
  }
  return out;
}

/** `rule:subject:anchor` — stable across runs, and readable in the message log during support. */
export function keyFor(rule: Pick<ReminderRule, "id" | "trigger" | "offsetDays">, subjectId: string, anchor: PlainDate): string {
  return `${rule.trigger}:${rule.offsetDays}:${rule.id}:${subjectId}:${anchor}`;
}

/**
 * Manual sends get their own key including the day, so a manager can chase the same tenant
 * again tomorrow — but two clicks on the same button in the same minute send one message.
 */
export function manualKey(subjectId: string, templateId: string, asOf: PlainDate): string {
  return `manual:${templateId}:${subjectId}:${asOf}`;
}

/* ------------------------------------------------------------- quiet hours */

export interface SendWindow {
  /** Local hour (0–23) from which sending is allowed. */
  fromHour: number;
  /** Local hour after which sending stops. */
  toHour: number;
  timeZone: string;
}

export const DEFAULT_SEND_WINDOW: SendWindow = { fromHour: 8, toHour: 20, timeZone: "UTC" };

/** Whether it is currently inside the agency's sending window. Automation respects this; a manual send does not. */
export function withinWindow(window: SendWindow, now: Date = new Date()): boolean {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", { timeZone: window.timeZone, hour: "2-digit", hour12: false }).format(now),
  );
  return window.fromHour <= window.toHour
    ? hour >= window.fromHour && hour < window.toHour
    : hour >= window.fromHour || hour < window.toHour;
}

/** When to retry a message that failed. Exponential with a ceiling, so a dead provider is not hammered. */
export function retryAt(attempts: number, now: Date = new Date()): Date {
  const minutes = Math.min(6 * 60, Math.round(2 ** attempts * 5));
  return new Date(now.getTime() + minutes * 60_000);
}

export const MAX_SEND_ATTEMPTS = 5;

/* --------------------------------------------------------- one message, or six */

/**
 * How many messages one person gets in a run.
 *
 * `per_invoice` is what the engine has always done: a message about an invoice, addressed to
 * whoever owes it. It is right for an agency whose clients hold one unit each, and it keeps the
 * message about a single, unambiguous thing.
 *
 * `per_person` folds the money triggers together, so a client with six units behind on all of
 * them receives one message listing all six with one total. It is right for an agency letting to
 * companies and to people who hold several shops in the same plaza — where six separate emails on
 * the same morning is precisely how an agency teaches its clients to stop reading its messages.
 *
 * **Neither is correct for everybody**, which is why this is a setting and not a decision.
 */
export const REMINDER_STYLES = ["per_invoice", "per_person"] as const;
export type ReminderStyle = (typeof REMINDER_STYLES)[number];

export const DEFAULT_REMINDER_STYLE: ReminderStyle = "per_invoice";

/** Copy for the setting, kept beside the values so the two cannot drift apart. */
export const REMINDER_STYLE_INFO: Array<{ value: ReminderStyle; label: string; description: string }> = [
  {
    value: "per_invoice",
    label: "One message for each invoice",
    description:
      "Each message concerns a single invoice and quotes its own reference, due date and amount. Suited to a portfolio where a client holds one unit.",
  },
  {
    value: "per_person",
    label: "One message for each client",
    description:
      "Everything a client owes is gathered into a single message, itemised, with one total. Suited to a portfolio where a client holds several units and would otherwise receive several messages in the same run.",
  },
];

/**
 * The triggers an aggregated message may combine.
 *
 * Deliberately the two that name a sum of money. A tenancy coming to an end is not something the
 * client *owes*, and folding it into a statement of arrears would produce a message that is about
 * two different things and reads as neither. Those keep their own message under both styles.
 */
const AGGREGATED_TRIGGERS = new Set<ReminderTrigger>(["rent_due", "rent_overdue"]);

/** One invoice on a client's account, as it stands at the moment the run is drawn. */
export interface OutstandingInvoice {
  invoiceId: string;
  partyId: string;
  number: string;
  dueDate: PlainDate;
  total: Minor;
  paid: Minor;
  unitLabel: string | null;
  propertyName: string | null;
  payToken: string | null;
}

export interface StatementLine {
  invoiceId: string;
  number: string;
  dueDate: PlainDate;
  balance: Minor;
  daysOverdue: number;
  unitLabel: string | null;
  propertyName: string | null;
  payToken: string | null;
}

export interface AggregatedReminder {
  partyId: string;
  /**
   * The planned reminder whose rule, template and channel the message is sent under.
   *
   * The furthest step of the sternest ladder that fired: an overdue chase outranks a courtesy
   * notice that rent is coming, and within a trigger the largest offset wins. Sending a client who
   * is six months behind on one unit the gentle "your rent falls due shortly" wording because that
   * rule happened to sort first would be worse than sending nothing.
   */
  primary: PlannedReminder;
  /**
   * Every planned reminder this one message discharges.
   *
   * All of their dedupe keys are recorded against the message, so the ladder advances exactly as
   * it would have under `per_invoice` and none of them can go out again — including after the
   * setting is switched back.
   */
  covers: PlannedReminder[];
  /** Everything the client owes, not only what a rule fired on today. */
  lines: StatementLine[];
  total: Minor;
}

export interface AggregationResult {
  aggregated: AggregatedReminder[];
  /** Planned reminders that are not folded in and are queued exactly as before. */
  passthrough: PlannedReminder[];
}

/**
 * Fold a run's planned reminders into one message per person.
 *
 * The planner is not changed and is not bypassed: it still decides who is chased, about what, and
 * how far down the ladder. This runs afterwards and decides only how many envelopes those
 * decisions are posted in — which is why turning aggregation on cannot chase somebody the engine
 * would not have chased, and turning it off cannot chase them twice.
 *
 * The statement lists everything the client currently owes rather than only the invoices that hit
 * a rule today. A client four invoices behind, three of which were chased last week, is owed one
 * honest total; a message that names one of the four and stays silent about the rest is the thing
 * this is meant to replace.
 */
export function aggregateReminders(input: {
  planned: readonly PlannedReminder[];
  outstanding: readonly OutstandingInvoice[];
  asOf: PlainDate;
}): AggregationResult {
  const aggregated: AggregatedReminder[] = [];
  const passthrough: PlannedReminder[] = [];
  const byParty = new Map<string, PlannedReminder[]>();

  for (const item of input.planned) {
    const foldable =
      AGGREGATED_TRIGGERS.has(item.trigger) && triggerInfo(item.trigger).audience === "tenant" && Boolean(item.invoiceId);
    if (!foldable) {
      passthrough.push(item);
      continue;
    }
    const existing = byParty.get(item.partyId);
    if (existing) existing.push(item);
    else byParty.set(item.partyId, [item]);
  }

  const outstandingByParty = new Map<string, OutstandingInvoice[]>();
  for (const invoice of input.outstanding) {
    if (balanceOf(invoice) <= 0) continue;
    const existing = outstandingByParty.get(invoice.partyId);
    if (existing) existing.push(invoice);
    else outstandingByParty.set(invoice.partyId, [invoice]);
  }

  for (const [partyId, covers] of byParty) {
    covers.sort(comparePlanned);
    const primary = covers[0];

    const lines = statementLines(outstandingByParty.get(partyId) ?? [], input.asOf);

    /*
      Nothing to itemise beyond the one invoice the rule fired on.

      This is the ordinary single-unit client, and for them the two styles are the same message.
      Passing it through rather than aggregating keeps it on its own dedupe key with no coverage
      rows behind it, so the log reads identically whichever setting the agency is on.
    */
    if (covers.length === 1 && lines.length <= 1) {
      passthrough.push(primary);
      continue;
    }

    aggregated.push({
      partyId,
      primary,
      covers,
      lines,
      total: lines.reduce((sum, line) => sum + line.balance, 0),
    });
  }

  return { aggregated, passthrough };
}

/**
 * The sternest first.
 *
 * Overdue outranks falling-due; within a trigger the largest offset is the furthest step of the
 * ladder and therefore the strongest wording the agency has authorised. The dedupe key breaks the
 * last tie so the choice is the same on every run and in the preview the manager was shown —
 * a single sort column is not a total order.
 */
function comparePlanned(a: PlannedReminder, b: PlannedReminder): number {
  const rank = (item: PlannedReminder) => (item.trigger === "rent_overdue" ? 0 : 1);
  return rank(a) - rank(b) || b.offsetDays - a.offsetDays || (a.dedupeKey < b.dedupeKey ? -1 : a.dedupeKey > b.dedupeKey ? 1 : 0);
}

/** Outstanding invoices as statement lines, oldest first, with the invoice id as the tie-break. */
export function statementLines(invoices: readonly OutstandingInvoice[], asOf: PlainDate): StatementLine[] {
  return invoices
    .filter((invoice) => balanceOf(invoice) > 0)
    .map((invoice) => ({
      invoiceId: invoice.invoiceId,
      number: invoice.number,
      dueDate: invoice.dueDate,
      balance: balanceOf(invoice),
      daysOverdue: Math.max(0, diffDays(invoice.dueDate, asOf)),
      unitLabel: invoice.unitLabel,
      propertyName: invoice.propertyName,
      payToken: invoice.payToken,
    }))
    .sort((a, b) =>
      a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : a.invoiceId < b.invoiceId ? -1 : a.invoiceId > b.invoiceId ? 1 : 0,
    );
}

/**
 * The itemised block appended to an aggregated message.
 *
 * Written out here rather than left to the agency's template, because a template cannot loop and
 * teaching it to would mean a second templating language for staff to learn. The agency's own
 * wording is still what the message opens with; this is the schedule underneath it, in the same
 * shape every time, so a client who receives one every month learns to read it at a glance.
 *
 * **No currency, country or region is named** — the formatter supplied by the caller decides how
 * an amount reads, exactly as it does everywhere else in the product.
 *
 * **A link per line, not one link for the lot.** A pay link is bound to a single invoice and is
 * refused above that invoice's balance, so a single link covering the whole total does not exist
 * and offering one would send the client to a page that rejects the figure quoted here.
 */
export function arrearsBlock(
  lines: readonly StatementLine[],
  opts: {
    money: (amount: Minor) => string;
    date: (day: PlainDate) => string;
    asOf: PlainDate;
    /** Absolute address for one invoice's payment page, or null where there is none. */
    payUrl?: (line: StatementLine) => string | null;
  },
): string {
  if (!lines.length) return "";

  const rows = lines.map((line, index) => {
    const place = [line.unitLabel, line.propertyName].filter(Boolean).join(", ");
    const head = `${index + 1}. Invoice ${line.number}${place ? ` — ${place}` : ""}`;
    const terms = [
      `Due ${opts.date(line.dueDate)}`,
      line.daysOverdue > 0 ? `${line.daysOverdue} day${line.daysOverdue === 1 ? "" : "s"} overdue` : "not yet due",
      opts.money(line.balance),
    ].join(" · ");
    const link = opts.payUrl?.(line);
    return `${head}\n   ${terms}${link ? `\n   Settle this invoice: ${link}` : ""}`;
  });

  const total = lines.reduce((sum, line) => sum + line.balance, 0);

  return [
    `Statement of amounts outstanding as at ${opts.date(opts.asOf)}`,
    "",
    rows.join("\n\n"),
    "",
    `Total outstanding: ${opts.money(total)} across ${lines.length} invoice${lines.length === 1 ? "" : "s"}.`,
  ].join("\n");
}

/**
 * The subject line for an aggregated message.
 *
 * The agency's template supplies the wording for a single invoice and cannot describe a set, so a
 * template that names `{{invoice.number}}` would produce a subject quoting one reference for a
 * message covering six. This replaces it, and only when the message is genuinely a set.
 */
export function aggregatedSubject(agencyName: string, count: number): string {
  return count === 1
    ? `${agencyName} — statement of your account`
    : `${agencyName} — statement of your account (${count} invoices outstanding)`;
}
